import React, { useState, useEffect } from 'react';
import ReactJoyride from 'react-joyride/lib/index';
import { STATUS, EVENTS, ACTIONS } from 'react-joyride/lib/index';
import { useLocation, useHistory } from 'react-router-dom';
import { getArticleMaintenanceNewTourSteps, tourSteps } from './tourSteps';
import { Button, Icon } from 'semantic-ui-react';
import { useUniversalLateralMenuHook } from 'hooks/useUniversalLateralMenuHook';
import { useDispatch, useSelector } from 'react-redux';
import userActions from 'store/actions/users';
import { showWarningMessage } from 'utils/messages';
import { useTranslation } from 'react-i18next';

const TOUR_RUNTIME_KEYS = {
    active: 'erp_tour_active',
    route: 'erp_tour_route',
    stepIndex: 'erp_tour_step_index',
};
const TOUR_CONTROL_EVENT = 'erp-tour-control';
const EMPTY_SEEN_ROUTES = [];

const isSidebarTarget = (target = '') =>
    target.includes('data-tour-group') ||
    target.includes('data-tour="subroute-') ||
    target.includes('data-tour="route-');

const isElementRendered = (element) => {
    if (!element) return false;
    const styles = window.getComputedStyle(element);
    if (styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) === 0) {
        return false;
    }
    if (element.hasAttribute('hidden')) return false;
    if (element.getAttribute('aria-hidden') === 'true') return false;
    return true;
};

const getVisibleElement = (selector) => {
    if (typeof selector !== 'string' || !selector.trim()) return null;
    try {
        const matches = Array.from(document.querySelectorAll(selector));
        if (matches.length === 0) return null;
        return matches.find((element) => isElementRendered(element)) || null;
    } catch {
        return null;
    }
};

const scrollSidebarToElement = (element) => {
    if (!element) return;
    const sidebar = element.closest('.sidebar') || document.querySelector('.sidebar');
    if (!sidebar) return;

    const sidebarRect = sidebar.getBoundingClientRect();
    const targetRect = element.getBoundingClientRect();
    const stickyHeader = sidebar.querySelector('.Sidebar__CloseStickyy');
    const stickyHeight = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
    const margin = 12;
    const visibleTop = sidebarRect.top + stickyHeight + margin;
    const visibleBottom = sidebarRect.bottom - margin;

    // If the target is already visible inside the sidebar viewport, do not move it.
    if (targetRect.top >= visibleTop && targetRect.bottom <= visibleBottom) {
        return;
    }

    let delta = 0;
    if (targetRect.top < visibleTop) {
        delta = targetRect.top - visibleTop;
    } else if (targetRect.bottom > visibleBottom) {
        delta = targetRect.bottom - visibleBottom;
    }

    sidebar.scrollTo({
        top: Math.max(sidebar.scrollTop + delta, 0),
        behavior: 'smooth',
    });
};

const normalizeSteps = (rawSteps = []) => {
    return rawSteps.map((step) => {
        const target = typeof step?.target === 'string' ? step.target : '';
        const isSidebarOrHeaderStep = isSidebarTarget(target);

        // `scrollToSteps` is not a valid step option in react-joyride v2.
        const { scrollToSteps, ...safeStep } = step;

        if (isSidebarOrHeaderStep) {
            return {
                ...safeStep,
                isFixed: true,
                disableScrolling: false,
            };
        }

        return safeStep;
    });
};

const getScrollableParent = (node) => {
    let current = node?.parentElement;

    while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style?.overflowY;
        const isScrollable =
            (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
            current.scrollHeight > current.clientHeight;

        if (isScrollable) {
            return current;
        }

        current = current.parentElement;
    }

    return null;
};

const getOffsetTopRelativeToAncestor = (node, ancestor) => {
    let offset = 0;
    let current = node;

    while (current && current !== ancestor) {
        offset += current.offsetTop || 0;
        current = current.offsetParent;
    }

    return offset;
};

const isSidebarTourTarget = (targetSelector, targetElement) => {
    const isSelectorFromSidebarTour =
        typeof targetSelector === 'string' &&
        (
            targetSelector.includes('data-tour-group') ||
            targetSelector.includes('data-tour="subroute-') ||
            targetSelector.includes('data-tour="route-') ||
            targetSelector.includes('data-tour=`subroute-') ||
            targetSelector.includes('data-tour=`route-')
        );

    const isElementInsideSidebar = !!targetElement?.closest?.('.sidebar');
    return isSelectorFromSidebarTour || isElementInsideSidebar;
};

const scrollSidebarStepIntoView = (targetSelector, behavior = 'auto') => {
    if (typeof targetSelector !== 'string') return;

    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) return;
    if (!isSidebarTourTarget(targetSelector, targetElement)) return;

    const sidebarRoot = targetElement.closest('.sidebar') || document.querySelector('.sidebar');
    // Preferir siempre el contenedor principal del sidebar para evitar scroll en contenedores internos.
    const scrollContainer = sidebarRoot || getScrollableParent(targetElement) || getScrollableParent(sidebarRoot);

    if (!scrollContainer || scrollContainer === document.body || scrollContainer === document.documentElement) {
        try {
            targetElement.scrollIntoView({ block: 'center', inline: 'nearest', behavior });
        } catch (error) {
            targetElement.scrollIntoView();
        }
        return;
    }

    const targetOffset = getOffsetTopRelativeToAncestor(targetElement, scrollContainer);
    const targetScrollTop =
        targetOffset - scrollContainer.clientHeight / 2 + targetElement.clientHeight / 2;

    scrollContainer.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior,
    });
};

const TourManager = () => {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const appUser = useSelector((state) => state.app?.user);
    const currentUserId = appUser?.id;
    const currentCompanyId = appUser?.sys_company_id;
    const canAccessTutorial = !!appUser?.can_access_tutorial;
    const seenRoutes = Array.isArray(appUser?.tour_seen_routes) ? appUser.tour_seen_routes : EMPTY_SEEN_ROUTES;

    const { t } = useTranslation();
    const [run, setRun] = useState(false);
    const [steps, setSteps] = useState([]);
    const { lateralMenu, setLateralMenu } = useUniversalLateralMenuHook('user');
    const [isPageReady, setIsPageReady] = useState(false);
    const [tourEnabled, setTourEnabledState] = useState(
        () => canAccessTutorial && (appUser?.tour_enabled ?? true)
    );
    const [stepsResolutionTick, setStepsResolutionTick] = useState(0);
    const [tourRunKey, setTourRunKey] = useState(0);

    useEffect(() => {
        if (run) {
            // Guardar el valor original del overflow
            const originalOverflow = document.body.style.overflow;
            const originalPaddingRight = document.body.style.paddingRight;

            // Calcular el ancho de la scrollbar para evitar "salto" visual
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

            // Bloquear scroll
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollbarWidth}px`;

            // Cleanup: restaurar cuando el tour termine
            return () => {
                document.body.style.overflow = originalOverflow;
                document.body.style.paddingRight = originalPaddingRight;
            };
        }
    }, [run]);

    const isTourEnabled = () => tourEnabled;

    const hasSeenTourRoute = (routeKey) => {
        if (!routeKey) return false;
        return seenRoutes.includes(routeKey);
    };

    const markTourSeen = async (routeKey) => {
        if (!routeKey || !currentUserId || !currentCompanyId) return;
        if (hasSeenTourRoute(routeKey)) return;

        const nextSeenRoutes = [...new Set([...seenRoutes, routeKey])];
        const payload = {
            userId: currentUserId,
            id: currentUserId,
            sys_company_id: currentCompanyId,
            tour_seen_routes: nextSeenRoutes,
        };

        await dispatch(userActions.updateTourSeenRoutesSilent(payload));
        dispatch({
            type: 'UPDATE_APP_USER',
            payload: { tour_seen_routes: nextSeenRoutes },
        });
    };

    const findMatchingTourKey = (path) => {
        return Object.keys(tourSteps)
            .sort((a, b) => b.length - a.length)
            .find((key) => path === key || (key !== '/' && path.startsWith(key)));
    };

    const resolveStepsForRoute = (matchingKey) => {
        if (!matchingKey) {
            return { steps: [], pending: false };
        }

        if (matchingKey === '/articles/maintenance/new') {
            const unitMeasureStateNode = document.querySelector('[data-tour="estado-unidades-medida"]');
            const isLoading =
                unitMeasureStateNode?.getAttribute('data-loading-units') === 'true';
            const hasUnitMeasures =
                unitMeasureStateNode?.getAttribute('data-has-units') === 'true';

            if (isLoading) {
                return { steps: [], pending: true };
            }

            return {
                steps: getArticleMaintenanceNewTourSteps({ hasUnitMeasures }),
                pending: false,
            };
        }

        return { steps: tourSteps[matchingKey] || [], pending: false };
    };

    const setTourRuntimeState = ({ active, route, stepIndex }) => {
        if (active !== undefined) {
            sessionStorage.setItem(TOUR_RUNTIME_KEYS.active, active ? 'true' : 'false');
        }
        if (route !== undefined) {
            sessionStorage.setItem(TOUR_RUNTIME_KEYS.route, route || '');
        }
        if (stepIndex !== undefined && Number.isInteger(stepIndex)) {
            sessionStorage.setItem(TOUR_RUNTIME_KEYS.stepIndex, String(stepIndex));
        }
    };

    const clearTourRuntimeState = () => {
        sessionStorage.removeItem(TOUR_RUNTIME_KEYS.active);
        sessionStorage.removeItem(TOUR_RUNTIME_KEYS.route);
        sessionStorage.removeItem(TOUR_RUNTIME_KEYS.stepIndex);
    };

    const stopTour = () => {
        setRun(false);
        clearTourRuntimeState();
    };

    useEffect(() => {
        if (!canAccessTutorial) {
            setTourEnabledState(false);
            stopTour();
            return;
        }

        setTourEnabledState(appUser?.tour_enabled ?? true);
    }, [canAccessTutorial, appUser?.tour_enabled]);

    useEffect(() => {
        const checkPageReady = () => {
            if (document.readyState === 'complete') {
                setIsPageReady(true);
            }
        };

        if (document.readyState === 'complete') {
            checkPageReady();
        } else {
            window.addEventListener('load', checkPageReady);
            return () => window.removeEventListener('load', checkPageReady);
        }
    }, []);

    useEffect(() => {
        const path = location.pathname;
        const matchingKey = findMatchingTourKey(path);
        const { steps: currentSteps, pending } = resolveStepsForRoute(matchingKey);

        if (pending) {
            const timer = setTimeout(() => {
                setStepsResolutionTick((prev) => prev + 1);
            }, 400);

            return () => clearTimeout(timer);
        }

        if (currentSteps && currentSteps.length > 0) {
            setSteps(normalizeSteps(currentSteps));
            if (!tourEnabled) {
                stopTour();
                return;
            }

            const stepsRequireSidebar = currentSteps.some((s) =>
                typeof s.target === 'string' &&
                (s.target.includes('data-tour') || s.target.includes('data-tour-group'))
            );

            if (isPageReady && !hasSeenTourRoute(matchingKey)) {
                const firstTarget = getVisibleElement(currentSteps[0].target);

                if (firstTarget) {
                    if (stepsRequireSidebar && typeof setLateralMenu === 'function') {
                        setLateralMenu(true);
                    }

                    const timer = setTimeout(() => {
                        setTourRuntimeState({ active: true, route: matchingKey, stepIndex: 0 });
                        setTourRunKey((prev) => prev + 1); // Forzar reinicio de Joyride para recalcular posiciones
                        setRun(true);
                    }, 1000);

                    return () => clearTimeout(timer);
                }
            }
        } else {
            // Avoid redundant state updates that can create render loops.
            setSteps((prev) => (prev.length > 0 ? [] : prev));
            setRun((prev) => (prev ? false : prev));
            clearTourRuntimeState();
        }
    }, [location.pathname, isPageReady, lateralMenu, tourEnabled, stepsResolutionTick, seenRoutes]);

    useEffect(() => {
        if (!run) return;
        const matchingKey = findMatchingTourKey(location.pathname);
        if (matchingKey) {
            setTourRuntimeState({ route: matchingKey });
        }
    }, [location.pathname, run]);

    const handleJoyrideCallback = (data) => {
        const { status, type, action, index } = data;
        const scrollStepIntoSidebar = (stepIndex) => {
            if (!Number.isInteger(stepIndex)) return;
            const step = steps[stepIndex];
            if (!step || typeof step.target !== 'string' || !isSidebarTarget(step.target)) return;
            const targetElement = getVisibleElement(step.target);
            scrollSidebarToElement(targetElement);
        };

        // ACTIONS.CLOSE (botón X) no cambia status a FINISHED/SKIPPED — lo manejamos explícitamente
        if (action === ACTIONS.CLOSE && type === EVENTS.STEP_AFTER) {
            stopTour();
            //Marca como visto el tour de la ruta actual al cerrar el tour manualmente, para no mostrarlo de nuevo automáticamente
            const matchingKey = findMatchingTourKey(location.pathname);
            if (matchingKey) {
                markTourSeen(matchingKey).catch((error) => {
                    console.error('Error guardando tour visto:', error);
                });
            }
            return;
        }

        if (type === EVENTS.STEP_BEFORE && Number.isInteger(index)) {
            setTourRuntimeState({ stepIndex: index });
            const target = steps[index]?.target;
            if (typeof target === 'string') {
                requestAnimationFrame(() => scrollSidebarStepIntoView(target, 'auto'));
                setTimeout(() => scrollSidebarStepIntoView(target, 'auto'), 120);
                setTimeout(() => scrollSidebarStepIntoView(target, 'auto'), 240);
            }
        }

        if (type === EVENTS.STEP_AFTER && Number.isInteger(index)) {
            let nextIndex = index;
            if (action === 'next') nextIndex = index + 1;
            if (action === 'prev') nextIndex = Math.max(index - 1, 0);
            setTourRuntimeState({ stepIndex: nextIndex });

            const target = steps[nextIndex]?.target;
            if (typeof target === 'string') {
                setTimeout(() => scrollSidebarStepIntoView(target, 'auto'), 120);
                setTimeout(() => scrollSidebarStepIntoView(target, 'auto'), 240);
            }
        }

        if (type === EVENTS.STEP_BEFORE && action === 'next') {
            const currentStep = steps[index];

            if (currentStep && typeof currentStep.target === 'string') {
                const isMainMenu = currentStep.target.includes('data-tour-group');
                const isSubMenuAccordion =
                    currentStep.target.includes('data-tour="subroute-') &&
                    !currentStep.target.match(/subroute-[^"]*\//);

                if (isMainMenu || isSubMenuAccordion) {
                    const element = getVisibleElement(currentStep.target);

                    if (element) {
                        const isAlreadyOpen =
                            element.classList.contains('active') ||
                            element.getAttribute('aria-expanded') === 'true';

                        if (!isAlreadyOpen) {
                            element.click();
                            setTimeout(() => {
                                scrollStepIntoSidebar(index + 1);
                            }, 280);
                        }
                    }
                }
            }
        }

        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            setRun(false);
            clearTourRuntimeState();
            const matchingKey = findMatchingTourKey(location.pathname);
            if (matchingKey) {
                markTourSeen(matchingKey).catch((error) => {
                    console.error('Error guardando tour visto:', error);
                });
            }

            // El sidebar permanece abierto hasta que el usuario lo desactive manualmente en Configuración
            const lastStepIndex = steps.length - 1;
            const lastStep = steps[lastStepIndex];
            if (status === STATUS.FINISHED && lastStep?.navigateTo && action !== ACTIONS.CLOSE) {
                // if (status === STATUS.FINISHED && lastStep?.navigateTo) {
                history.push(lastStep.navigateTo);
                // Disparar el tour en la página destino igual que si el usuario
                // hiciera click en "Ver Tutorial" (startTour no verifica hasSeenTourRoute)
                setTimeout(() => {
                    window.dispatchEvent(
                        new CustomEvent(TOUR_CONTROL_EVENT, { detail: { action: 'start-current' } })
                    );
                }, 1000);
            }
        }
    };

    const startTour = () => {
        if (!isTourEnabled()) {
            return;
        }

        const path = location.pathname;
        const matchingKey = findMatchingTourKey(path);

        if (matchingKey) {
            const { steps: targetSteps, pending } = resolveStepsForRoute(matchingKey);

            if (pending) {
                showWarningMessage(
                    t('Los elementos del tour no estan disponibles aun. Por favor espera a que la pagina cargue.')
                );
                return;
            }

            setSteps(normalizeSteps(targetSteps));

            const stepsRequireSidebar = targetSteps.some((s) =>
                typeof s.target === 'string' &&
                (s.target.includes('data-tour') || s.target.includes('data-tour-group'))
            );

            if (stepsRequireSidebar && typeof setLateralMenu === 'function') {
                setLateralMenu(true);
            }

            const firstTarget = getVisibleElement(targetSteps[0].target);
            if (firstTarget) {
                setTourRuntimeState({ active: true, route: matchingKey, stepIndex: 0 });
                setTourRunKey((prev) => prev + 1); // Forzar reinicio de Joyride para recalcular posiciones
                setRun(true);
            } else {
                showWarningMessage(
                    t('Los elementos del tour no estan disponibles aun. Por favor espera a que la pagina cargue.')
                );
            }
        } else {
            showWarningMessage(t('No hay un tutorial especifico para esta pagina aun.'));
        }
    };

    useEffect(() => {
        const handleTourControl = (event) => {
            const action = event?.detail?.action;
            if (action === 'disable-all') {
                setTourEnabledState(false);
                stopTour();
                return;
            }

            if (action === 'enable-all') {
                setTourEnabledState(true);
                return;
            }

            if (action === 'start-current') {
                if (!canAccessTutorial) return;
                startTour();
            }
        };

        window.addEventListener(TOUR_CONTROL_EVENT, handleTourControl);
        return () => window.removeEventListener(TOUR_CONTROL_EVENT, handleTourControl);
    }, [location.pathname, lateralMenu, steps, tourEnabled, canAccessTutorial]);

    if (!canAccessTutorial) return null;
    if (steps.length === 0) return null;

    return (
        <>
            <ReactJoyride
                key={`${location.pathname}-${tourRunKey}`} // Forzar remount al cambiar de ruta o reiniciar tour
                steps={steps}
                run={run}
                continuous={true}
                showProgress={true}
                showSkipButton={false}
                disableBeacon={true}
                callback={handleJoyrideCallback}
                disableScrollParentFix={false}
                scrollOffset={100}
                scrollDuration={0}
                showCloseButton={false}
                disableOverlay={false}
                disableOverlayClose={true}
                spotlightClicks={false}
                styles={{
                    options: {
                        zIndex: 850,
                        primaryColor: '#0052CC',
                    },
                }}
                locale={{
                    back: 'Atras',
                    close: 'Cerrar',
                    last: steps[steps.length - 1]?.isAbsoluteLast ? 'Finalizar' : 'Siguiente',
                    next: 'Siguiente',
                    skip: 'Omitir',
                }}
            />
            {/* {!run && steps.length > 0 && tourEnabled && (
                <div style={{ position: 'fixed', top: '65px', right: '20px', zIndex: 840 }}>
                    <Button circular icon color="blue" onClick={startTour} title={t("Ver Tutorial")}>
                        <Icon name="help" />
                    </Button>
                </div>
            )} */}
        </>
    );
};

export default TourManager;
